<template>
	<div class="user-analytics-container">

		<UserInitialCard 
			ref="initial-card" 
			:aggregated-data="todayAggregatedOrders"
			:previous-aggregated-data="todayPreviousAggregatedOrders"
			:show-live-badge="shouldEnableAutoRefresh"
		/>

		<div ref="selectors-row" class="row selectors-row" :class="{collapsed: collapsedSelectors, showing: surpassedFilters && !collapsedSelectors}">
<!--			<AppChecklist
				v-if="!!availableBusinesses && availableBusinesses.length < 2"
				class="businesses-checklist"
				:label="getString('businesses')"
				:title="getString('businesses')"
				:values="businessesNames"
				:selectedIndexes="selectedBusinessesIndexes"
				@change-checked="changedBusinesses"
			/>-->
			<div class="period-picker">
				<AppSelect
					:label="getString('select_period')"
					:values="timeNames"
					:selected-index="selectedIndex"
					@selected="changedTimeframe"
				/>
				<DatePicker ref="date-picker" v-model.range="selectedRange" @update:modelValue="changedCalendarDates" color="purple" :is-dark="userStore.darkMode">
					<template #default="{ togglePopover }">
						<div ref="period-phrases" class="period-phrases" @click="togglePopover">
							<p class="period">{{periodPhrase}}</p>
							<p v-show="showCharts" class="old-period">{{oldPeriodPhrase}}</p>
						</div>
					</template>
				</DatePicker>
			</div>
			<AppSwitch
				fixed-color
				left-icon="iconChart"
				right-icon="iconTable"
				@switched="switchChartsTables"
			/>
		</div>

		<UserChartsAggregated 
			v-if="showCharts" 
			ref="user-charts" 
			:aggregated-data="aggregatedOrders" 
			:previous-aggregated-data="previousAggregatedOrders"
			:views="views" 
			:previous-views="previousViews" 
			:period-dates="periodDatesObject"
			:time-grain="timeGrain"
			:bucket-column="bucketColumn"
			:is-aggregate-only="isAggregateOnly"
		/>
		<UserTablesAggregated
			v-else
			:aggregated-data="aggregatedOrders"
			:businesses="selectedBusinesses"
			:period-dates="periodDatesObject"
			:is-aggregate-only="isAggregateOnly"
		/>

		<AppButton v-if="superStore.businesses && superStore.businesses.length >= 8" :text="getString('businesses_overview')" outline class="full-width-button" @click="$router.push('/overview-businesses')" />

	</div>
</template>

<script>
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import UserInitialCard from "@/user/components/analytics/UserInitialCard.vue";
import UserChartsAggregated from "@/user/components/analytics/UserChartsAggregated.vue";
import UserTablesAggregated from "@/user/components/analytics/UserTablesAggregated.vue";

import {useSuperStore} from "@/user/stores";
import {useLanguageStore} from "@/shared/stores";
import {formatDateTime, formatDateTimeWithSeconds, getLocalDateFromServer, getCompatibleDate, formatDateOnly} from "@/shared/helpers/dates";

import API from '@/shared/helpers/API.js';
import {DatePicker} from "v-calendar";
import {useAnalyticsStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import AppButton from "@/shared/components/AppButton.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const superStore = useSuperStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		return {formatDateTime, getString: languageStore.getString, languageStore, superStore, analyticsStore, userStore}
	},
	name: "UserAnalytics",
	components: {AppButton, AppSelect, AppChecklist, AppSwitch, UserChartsAggregated, UserTablesAggregated, UserInitialCard, DatePicker},
	inject: ["providedAction"],
	data() {
		return {
			realtimeTimeout: null,
			selectedBusinessesIndexes: [],
			timeNames: [
				this.getString('1_week'),
				this.getString('4_weeks'),
				this.getString('this_month'),
				this.getString('3_months'),
				this.getString('6_months'),
				this.getString('1_year'),
				this.getString('this_year'),
				this.getString('custom'),
			],
			aggregatedOrders: null,
			previousAggregatedOrders: null,
			views: [],
			previousViews: [],
			todayAggregatedOrders: null,
			todayPreviousAggregatedOrders: null,
			timeGrain: null,
			bucketColumn: null,
			isAggregateOnly: false,
			loadingOrdersAggregated: false,
			reloadOrdersAggregated: false,
			showCharts: true,
			collapsedSelectors: false,
			scrollLen: 0,
			selectedRange: {
				start: new Date(),
				end: new Date()
			},
			selectedIndex: 0
		};
	},
	computed: {
		periodDatesObject() {
			return {
				end: this.targetEndDate,
				start: this.targetStartDate,
				previousStart: this.targetPreviousStartDate,
				previousEnd: this.targetPreviousEndDate,
				timeframeIndex: this.selectedIndex,
			}
		},
		availableBusinesses() {
			if(!this.superStore.businesses || !this.superStore.businesses.length) { return []; }
			return this.superStore.businesses;
		},
		selectedBusinesses() {
			return this.selectedBusinessesIndexes.map(index => this.availableBusinesses[index]);
		},
		selectedBusinessesIds() {
			if(!this.selectedBusinesses || !this.selectedBusinesses.length) { return []; }
			return this.selectedBusinesses.map(business => {
				if(business && business.business_id) {
					return business.business_id;
				}
			});
		},
		businessesNames() {
			return this.availableBusinesses.map(business => business.name + ' (' + business.link + ')');
		},
		targetStartDate() {
			if(this.selectedIndex === 7) {
				let start = new Date(this.selectedRange.start);
				start.setHours(3, 0, 0, 0);
				return start;
			} else {
				return this.analyticsStore.getTargetStartDate(this.selectedIndex);
			}
		},
		targetEndDate() {
			if(this.selectedIndex === 7) {
				let end = new Date(this.selectedRange.end);
				end.setDate(end.getDate() + 1);
				end.setHours(3, 0, 0, 0);
				return end;
			} else {
				return this.analyticsStore.getTargetEndDate();
			}
		},
		targetPreviousStartDate() {
			if(this.selectedIndex === 7) {
				let dateDiff = this.targetEndDate.getTime() - this.targetStartDate.getTime();
				return new Date(this.targetStartDate.getTime() - dateDiff);
			} else {
				return this.analyticsStore.getTargetPreviousStartDate(this.selectedIndex);
			}
		},
		targetPreviousEndDate() {
			if(this.selectedIndex === 7) {
				let startDate = new Date(this.targetStartDate);
				startDate.setMinutes(startDate.getMinutes() - 1);
				return startDate;
			} else {
				return this.analyticsStore.getTargetPreviousEndDate(this.selectedIndex);
			}
		},
		periodPhrase() {
			let endDateDB = new Date(this.targetEndDate);
			let startDateDB = this.targetStartDate;
			endDateDB.setDate(endDateDB.getDate() - 1);
			let fullYear = endDateDB.getFullYear() !== startDateDB.getFullYear() || endDateDB.getFullYear() !== new Date().getFullYear() || [5,6].includes(this.selectedIndex);
			return this.localeDateString(startDateDB, fullYear) + ' - ' + (this.selectedIndex === 7 ? this.localeDateString(endDateDB, fullYear) : this.getString('today'));
		},
		oldPeriodPhrase() {
			let endDateDB = new Date(this.targetPreviousEndDate);
			let startDateDB = this.targetPreviousStartDate;
			endDateDB.setDate(endDateDB.getDate() - 1);
			let fullYear = endDateDB.getFullYear() !== startDateDB.getFullYear() || endDateDB.getFullYear() !== new Date().getFullYear() || [5,6].includes(this.selectedIndex);
			return this.localeDateString(startDateDB, fullYear) + ' - ' + this.localeDateString(endDateDB, fullYear);
		},
		surpassedFilters() {
			let initialCard = this.$refs['initial-card'] && this.$refs['initial-card'].$el ? this.$refs['initial-card'].$el : null;
			let targetScroll = initialCard && initialCard.offsetHeight ? initialCard.offsetHeight : 0;
			targetScroll += 100;
			return this.scrollLen >= targetScroll;
		},
		shouldEnableAutoRefresh() {
			// Enable auto-refresh only for super users with 10 or fewer businesses
			return this.superStore.isSuper || this.availableBusinesses.length <= 10;
		},
	},
	methods: {
		changedCalendarDates() {
			this.selectedIndex = 7;
			setTimeout(() => {
				this.fetchOrdersAggregated();
			}, 850);
		},
		changedTimeframe(newIndex) {
			this.selectedIndex = newIndex;
			if(newIndex === 7) {
				this.$refs['date-picker'].showPopover({showDelay: 550});
			} else {
				this.fetchOrdersAggregated();
			}
		},
		changedBusinesses(newIndexes) {
			this.selectedBusinessesIndexes = newIndexes;
			this.fetchOrdersAggregated();
			this.fetchTodayOrders();
		},
		firstDayOfCurrentMonth() {
			const date = new Date();
			return new Date(date.getFullYear(), date.getMonth(), 1, 3, 0, 0, 0);
		},
		firstDayOfCurrentYear() {
			const date = new Date();
			return new Date(date.getFullYear(), 0, 1, 3, 0, 0, 0);
		},
		firstDayOfLastMonth() {
			const date = this.firstDayOfCurrentMonth();
			date.setMonth(date.getMonth() - 1);
			return date;
		},
		firstDayOfLastYear() {
			const date = this.firstDayOfCurrentYear();
			date.setFullYear(date.getFullYear() - 1);
			return date;
		},
		fetchTodayOrders(callback = null) {
			// Only fetch aggregated data
			this.fetchTodayOrdersAggregated(callback);
		},
		fetchTodayOrdersAggregated(callback = null) {
			let now = new Date();
			// Fetch from yesterday's day shift to cover both today and yesterday's same period
			let startDate = this.analyticsStore.getPreviousDayShiftDatetime();

			let endDateDB = formatDateTime(new Date());
			let startDateDB = formatDateTime(startDate);
			
			// For mega/ultra users, send null to get aggregated data across all businesses
			const businessIdsToSend = (this.superStore.isUltra || this.superStore.isMega) ? null : this.selectedBusinessesIds;
			API.init().superStatsOrdersAggregated(startDateDB, endDateDB, businessIdsToSend, response => {
				if (response && response.result === "OK") {
					// Split the aggregated data between today and yesterday based on day shift
					const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
					const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();

					const currentData = { full: [] };
					const previousData = { full: [] };

					if (response.aggregated_data && response.aggregated_data.full) {
						// First normalize the data
						const normalizedData = this.normalizeAggregatedData(response.aggregated_data.full, response.bucket_column);

						// Calculate the current time (rounded up to next hour)
						const now = new Date();
						const nowRounded = new Date(now);
						if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
							nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
						}
						nowRounded.setMinutes(0, 0, 0);

						// Calculate elapsed time for previous period comparison
						const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
						const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

						// Then split based on normalized dates
						normalizedData.forEach(row => {
							const bucket = new Date(row[response.bucket_column || 'hour_bucket']);
							// hour_bucket represents the END of the hour, so exclude the shift hour itself
							if (bucket > todayShiftStart && bucket <= nowRounded) {
								currentData.full.push(row);
							} else if (bucket > yesterdayShiftStart && bucket <= yesterdayEndTime) {
								previousData.full.push(row);
							}
						});
					}
					
					this.todayAggregatedOrders = currentData;
					this.todayPreviousAggregatedOrders = previousData;
				}
				
				if (callback) {
					callback();
				}
			});
		},
		fetchOrdersAggregated() {
			if (this.loadingOrdersAggregated) {
				this.reloadOrdersAggregated = true;
				return;
			}
			this.loadingOrdersAggregated = true;
			
			let endDateDB = formatDateTime(this.targetEndDate);
			let startDateDB = formatDateTime(this.targetStartDate);
			let previousEndDateDB = formatDateTime(this.targetPreviousEndDate);
			let previousStartDateDB = formatDateTime(this.targetPreviousStartDate);
			
			// For mega/ultra users, send null to get aggregated data across all businesses
			const businessIdsToSend = (this.superStore.isUltra || this.superStore.isMega) ? null : this.selectedBusinessesIds;
			
			// Fetch aggregated data for current and previous periods
			let loadingCurrent = true;
			let loadingPrevious = true;
			let aggregatedData = null;
			let previousAggregatedData = null;
			
			const completeFetch = () => {
				if (!loadingCurrent && !loadingPrevious) {
					this.loadingOrdersAggregated = false;
					this.aggregatedOrders = aggregatedData;
					this.previousAggregatedOrders = previousAggregatedData;
					
					if (this.reloadOrdersAggregated) {
						this.reloadOrdersAggregated = false;
						this.fetchOrdersAggregated();
					}
				}
			};
			
			// Check if we need separate calls for current and previous periods
			const daysDiff = this.analyticsStore.getDayDifference(this.targetStartDate, this.targetEndDate);
			if ([2, 6].includes(this.selectedIndex) || (this.selectedIndex === 7 && daysDiff > 70)) {
				// Fetch current period
				API.init().superStatsOrdersAggregated(startDateDB, endDateDB, businessIdsToSend, response => {
					if (response && response.result === "OK") {
						aggregatedData = {
							full: this.normalizeAggregatedData(response.aggregated_data?.full, response.bucket_column),
							type: this.normalizeAggregatedData(response.aggregated_data?.type, response.bucket_column),
							mode: this.normalizeAggregatedData(response.aggregated_data?.mode, response.bucket_column),
						};
						this.timeGrain = response.time_grain;
						this.bucketColumn = response.bucket_column;
						this.isAggregateOnly = response.is_aggregate_only || false;
						this.views = response.views ? this.normalizeViews(response.views) : [];
					}
					loadingCurrent = false;
					completeFetch();
				});
				
				// Fetch previous period
				API.init().superStatsOrdersAggregated(previousStartDateDB, previousEndDateDB, businessIdsToSend, response => {
					if (response && response.result === "OK") {
						// Normalize aggregated data for proper date handling
						previousAggregatedData = {
							full: this.normalizeAggregatedData(response.aggregated_data?.full, response.bucket_column),
							type: this.normalizeAggregatedData(response.aggregated_data?.type, response.bucket_column),
							mode: this.normalizeAggregatedData(response.aggregated_data?.mode, response.bucket_column),
						};
						this.previousViews = response.views ? this.normalizeViews(response.views) : [];
					}
					loadingPrevious = false;
					completeFetch();
				});
			} else {
				// Other timeframes - single call covers both periods
				loadingPrevious = false; // We won't make a second call
				
				API.init().superStatsOrdersAggregated(previousStartDateDB, endDateDB, businessIdsToSend, response => {
					if (response && response.result === "OK") {
						// We need to split the data between current and previous periods
						const currentData = { full: [], type: [], mode: [] };
						const previousData = { full: [], type: [], mode: [] };
						
						// Split the aggregated data based on date ranges
						if (response.aggregated_data) {
							// First normalize all data
							const normalizedData = {
								full: this.normalizeAggregatedData(response.aggregated_data.full, response.bucket_column),
								type: this.normalizeAggregatedData(response.aggregated_data.type, response.bucket_column),
								mode: this.normalizeAggregatedData(response.aggregated_data.mode, response.bucket_column),
							};
							
							// Then split based on normalized dates
							['full', 'type', 'mode'].forEach(dimension => {
								if (normalizedData[dimension]) {
									normalizedData[dimension].forEach(row => {
										const bucket = new Date(row[response.bucket_column || 'day_bucket']);
										if (bucket >= this.targetStartDate && bucket < this.targetEndDate) {
											currentData[dimension].push(row);
										} else if (bucket >= this.targetPreviousStartDate && bucket < this.targetStartDate) {
											previousData[dimension].push(row);
										}
									});
								}
							});
						}
						
						aggregatedData = currentData;
						previousAggregatedData = previousData;
						this.timeGrain = response.time_grain;
						this.bucketColumn = response.bucket_column;
						this.isAggregateOnly = response.is_aggregate_only || false;
						
						// Split views between current and previous
						let allViews = response.views ? this.normalizeViews(response.views) : [];
						this.views = [];
						this.previousViews = [];
						allViews.forEach(view => {
							if (this.targetStartDate <= view.date && view.date < this.targetEndDate) {
								this.views.push(view);
							} else if (this.targetPreviousStartDate <= view.date && view.date < this.targetStartDate) {
								this.previousViews.push(view);
							}
						});
					}
					loadingCurrent = false;
					completeFetch();
				});
			}
		},
		normalizeViews(views) {
			if (!views || !views.length) return [];
			
			views.forEach(view=>{
				view.date = getLocalDateFromServer(view.ds);
				view.visits_count = view.vc ? Number(view.vc) : 0;
				view.users_count = view.uc ? Number(view.uc) : 0;
				delete view.ds;
				delete view.vc;
				delete view.uc;
			});
			views.sort((a, b) => {
				return a.date - b.date;
			});
			return views;
		},
		resetSelectedBusinesses() {
			this.selectedBusinessesIndexes = [];
			for(let i = 0; i < this.availableBusinesses.length; i++) {
				this.selectedBusinessesIndexes.push(i);
			}
			this.fetchTodayOrders();
			this.fetchOrdersAggregated();
		},
		localeDateString(date, fullYear = false) {
			let options = {month: 'long', day: 'numeric'};
			if(fullYear) {
				options.year = 'numeric';
				options.month = 'short';
			}
			return date.toLocaleDateString([this.languageStore.lang || 'en-US'], options);
		},
		switchChartsTables() {
			this.showCharts = !this.showCharts;
			setTimeout(() => {
				if(this.showCharts) {
					this.$refs['user-charts'].resetCharts();
				}
			}, 200);
		},
		normalizeAggregatedData(data, bucketColumn = 'day_bucket') {
			// Adjust dates from calendar day to business day
			// Redshift groups by calendar day, but UI expects business days
			// Business day runs from 3am to 3am, so calendar day data needs +1 day adjustment
			if (!data) return data;
			
			data.forEach(row => {
				// Normalize numeric fields to ensure they're numbers, not strings
				Object.keys(row).forEach(key => {
					if (key !== bucketColumn && row[key] !== null && row[key] !== undefined) {
						const numericFields = ['units_sold', 'gross_sales', 'net_total', 'discounts_total',
											  'order_count', 'events_count', 'visits_count', 'total',
											  'vat_total', 'service_charge_total', 'tips_total'];
						if (numericFields.some(field => key.includes(field)) ||
							(!isNaN(row[key]) && typeof row[key] === 'string')) {
							row[key] = Number(row[key]);
						}
					}
				});

				if (bucketColumn === 'hour_bucket') {
					// For hour_bucket, convert from server UTC to local timezone
					const localDate = getLocalDateFromServer(row[bucketColumn]);
					// Add 1 hour to shift the data forward
					localDate.setHours(localDate.getHours() + 1);
					// Keep the timestamp in ISO format to preserve hours
					row[bucketColumn] = localDate.toISOString();
				} else if (bucketColumn === 'day_bucket') {
					// For day bucket, parse the date and add 1 day for business day adjustment
					const date = getCompatibleDate(row[bucketColumn]);
					date.setDate(date.getDate() + 1);
					// Store back as YYYY-MM-DD format
					row[bucketColumn] = formatDateOnly(date);
				} else if (bucketColumn === 'month_bucket') {
					// For month bucket, keep the date as-is (1st of month)
					// No adjustment needed since we want to align with x-axis dates
				}
			});
			
			return data;
		},
		scrollListener() {
			let scrollItem = document.querySelector('.main-content');
			this.scrollLen = scrollItem && scrollItem.scrollTop ? scrollItem.scrollTop : 0;
		},
		scheduleNextRefresh() {
			// Clear any existing timeout
			if(this.realtimeTimeout) {
				clearTimeout(this.realtimeTimeout);
			}
			
			// Only schedule if auto-refresh should be enabled
			if (!this.shouldEnableAutoRefresh) {
				return;
			}
			
			// Schedule the next refresh in 20 seconds
			this.realtimeTimeout = setTimeout(() => {
				this.fetchTodayOrders(() => {
					// Schedule the next refresh only after fetch completes
					this.scheduleNextRefresh();
				});
			}, 1000 * 20);
		}
	},
	mounted() {
		if(this.superStore.businesses === null) {
			this.superStore.fetchBusinesses(this.resetSelectedBusinesses);
		} else {
			this.resetSelectedBusinesses();
		}
		this.scrollListener();
		let mainContent = document.querySelector('.main-content');
		if(mainContent) {
			mainContent.addEventListener('scroll', this.scrollListener);
		}
		
		// Set up auto-refresh only for super users with 10 or fewer businesses
		if (this.shouldEnableAutoRefresh) {
			this.scheduleNextRefresh();
		}
	},
	beforeUnmount() {
		let mainContent = document.querySelector('.main-content');
		if(mainContent && this.scrollListener) {
			mainContent.removeEventListener('scroll', this.scrollListener);
		}
		
		// Clear the auto-refresh timeout
		if(this.realtimeTimeout) {
			clearTimeout(this.realtimeTimeout);
		}
	},
	watch: {
		providedAction(newProvidedObject) {
			if(newProvidedObject.hasOwnProperty('collapsedSelectors') && newProvidedObject.collapsedSelectors !== this.collapsedSelectors) {
				this.collapsedSelectors = newProvidedObject.collapsedSelectors;
			}
		},
		surpassedFilters(newState) {
			if(this.providedAction && this.providedAction.toggleIconFilters) {
				this.providedAction.toggleIconFilters(!!newState);
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.user-analytics-container {
	max-width: 1400px;
	margin: 0 auto;
	padding: var(--main-padding);

	.selectors-row {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
		margin: 30px 0 20px calc(-1 * var(--main-padding));
		backdrop-filter: blur(0px);
		position: sticky;
		box-shadow: 0 1px 10px rgb(7 82 163 / 0%);
		top: 0;
		background: var(--color-bg-primary-1);
		z-index: 13;
		width: calc(100% + (var(--main-padding)*2));
		padding: 10px var(--main-padding);
		transition: top .3s ease, box-shadow .3s ease, background .3s ease;

		&.collapsed {
			top: -125px;
		}

		.switch {
			margin-left: auto;
			margin-top: 10px;
		}

		.period-picker {
			display: flex;
			width: calc(100% - 190px - 92px);

			.period-phrases {
				display: flex;
				flex-direction: column;
				align-self: stretch;
				justify-content: center;
				padding-left: 20px;
				padding-right: 20px;
				padding-top: 20px;
				cursor: pointer;

				p.period, p.old-period {
					opacity: .8;
					white-space: nowrap;
					font-size: 12px;
					line-height: 14px;
				}
				p.old-period {
					color: #c0c0c0;
				}
			}
		}

		.select-wrapper {
			max-width: 190px;
		}
		&.showing {
			box-shadow: 0 1px 10px var(--color-box-shadow-3);
			backdrop-filter: blur(4px);
			background: rgba(var(--rgb-bg-primary-1), 67%);
		}
		.businesses-checklist {
			:deep(small) {
				color: var(--color-txt-primary-2);
				font-size: 10px;
			}
		}

	}

	@media (min-width: 1550px) {
		.selectors-row {
			border-bottom-left-radius: 13px;
			border-bottom-right-radius: 13px;
		}
	}
	@media (max-width: 1440px) {
		.selectors-row {
			top: -35px;
			&.collapsed {
				top: -115px;
			}
		}
	}
	@media (max-width: 1080px) {
		.selectors-row {
			top: -25px;
			&.collapsed {
				top: -105px;
			}
		}
	}
	@media (max-width: 820px) {
		.selectors-row {

			.switch {
				order: 2;
				margin-left: auto;
			}

			.period-picker {
				order: 3;
				padding-left: 0;
				padding-top: 10px;
				width: 100%;
			}

			&.collapsed {
				top: -175px;
			}
		}
	}
	@media (max-width: 768px) {
		.selectors-row {
			top: -20px;
		}
	}
	@media (max-width: 420px) {
		.selectors-row {
			top: -15px;

			.period-picker {
				flex-direction: column;
				align-items: flex-start;
				justify-content: center;

				.period-phrases {
					padding-left: 0;
					padding-top: 10px;
					padding-right: 10px;
				}
			}

			&.collapsed {
				top: -210px;
			}
		}
	}

	@media (max-width: 380px) {
		.selectors-row {
			>.select-wrapper {
				width: calc(100% - 80px);
			}
		}
	}

	.full-width-button {
		margin-top: var(--main-padding);
		width: 100%;
		max-width: 100%;
	}
}
</style>